/**
 * Email Tester
 * Component for testing email templates on-demand
 */

import { useState, useEffect } from 'react'
import { Button } from '@/components/admin/ui/button'
import { Input } from '@/components/admin/ui/input'
import { Label } from '@/components/admin/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/admin/ui/select'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/admin/ui/card'
import { useToast } from '@/hooks/use-toast'
import { emailTestService, EmailTypes } from '@/services/emailTestService'
import { Loader2, Send, Mail, Info } from 'lucide-react'
import { format, addDays } from 'date-fns'

interface EmailTesterProps {
  adminEmail: string
}

// Default values for each param type
const getDefaultValue = (param: string): string => {
  const defaults: Record<string, string> = {
    customerName: 'Test Customer',
    photographerName: 'Test Photographer',
    talentName: 'Test Photographer',
    eventDate: format(addDays(new Date(), 14), 'yyyy-MM-dd'),
    eventTime: '14:00',
    oldDate: format(new Date(), 'yyyy-MM-dd'),
    newDate: format(addDays(new Date(), 7), 'yyyy-MM-dd'),
    location: 'Austin, TX',
    eventLocation: 'Austin, TX',
    locationCity: 'Austin',
    locationState: 'TX',
    venueAddress: '123 Test Venue St, Austin, TX 78701',
    amountPaid: '1500',
    packageType: 'Photo Only',
    daysUntilEvent: '14',
    daysSinceEvent: '7',
    bookingId: 'test-booking-id',
    courseName: 'Photography Fundamentals',
    isEmergency: 'false'
  }
  return defaults[param] || ''
}

export const EmailTester = ({ adminEmail }: EmailTesterProps) => {
  const { toast } = useToast()
  const [loading, setLoading] = useState(false)
  const [loadingTypes, setLoadingTypes] = useState(true)
  const [emailTypes, setEmailTypes] = useState<EmailTypes>({})
  const [selectedType, setSelectedType] = useState<string>('')
  const [params, setParams] = useState<Record<string, string>>({})
  const [recipientEmail, setRecipientEmail] = useState(adminEmail)

  useEffect(() => {
    loadEmailTypes()
  }, [])

  useEffect(() => {
    // Initialize params with defaults when email type changes
    if (selectedType && emailTypes[selectedType]) {
      const newParams: Record<string, string> = {}
      emailTypes[selectedType].params.forEach(param => {
        newParams[param] = getDefaultValue(param)
      })
      setParams(newParams)
    }
  }, [selectedType, emailTypes])

  const loadEmailTypes = async () => {
    setLoadingTypes(true)
    try {
      const types = await emailTestService.getEmailTypes()
      setEmailTypes(types)
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load email types',
        variant: 'destructive'
      })
    } finally {
      setLoadingTypes(false)
    }
  }

  const handleParamChange = (param: string, value: string) => {
    setParams(prev => ({ ...prev, [param]: value }))
  }

  const handleSendEmail = async () => {
    if (!selectedType) {
      toast({
        title: 'Error',
        description: 'Please select an email type',
        variant: 'destructive'
      })
      return
    }

    if (!recipientEmail || !recipientEmail.includes('@')) {
      toast({
        title: 'Error',
        description: 'Please enter a valid email address',
        variant: 'destructive'
      })
      return
    }

    setLoading(true)
    try {
      const result = await emailTestService.sendTestEmail(selectedType, recipientEmail, params)
      toast({
        title: 'Success',
        description: result.message
      })
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to send test email',
        variant: 'destructive'
      })
    } finally {
      setLoading(false)
    }
  }

  const formatParamLabel = (param: string): string => {
    return param
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase())
      .trim()
  }

  if (loadingTypes) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        <span className="ml-2 text-muted-foreground">Loading email types...</span>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Email Type Selection */}
      <div className="space-y-2">
        <Label htmlFor="emailType">Email Type</Label>
        <Select value={selectedType} onValueChange={setSelectedType}>
          <SelectTrigger id="emailType" className="w-full">
            <SelectValue placeholder="Select an email type..." />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(emailTypes).map(([key, type]) => (
              <SelectItem key={key} value={key}>
                <div className="flex flex-col">
                  <span>{type.label}</span>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {selectedType && emailTypes[selectedType] && (
          <p className="text-sm text-muted-foreground flex items-center gap-1">
            <Info className="h-3 w-3" />
            {emailTypes[selectedType].description}
          </p>
        )}
      </div>

      {/* Dynamic Parameters Form */}
      {selectedType && emailTypes[selectedType] && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Email Parameters</CardTitle>
            <CardDescription>
              Fill in the parameters for this email. Default test values are provided.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {emailTypes[selectedType].params.map(param => (
                <div key={param} className="space-y-2">
                  <Label htmlFor={param}>{formatParamLabel(param)}</Label>
                  <Input
                    id={param}
                    value={params[param] || ''}
                    onChange={(e) => handleParamChange(param, e.target.value)}
                    placeholder={formatParamLabel(param)}
                  />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recipient Email */}
      <div className="space-y-2">
        <Label htmlFor="recipientEmail" className="flex items-center gap-2">
          <Mail className="h-4 w-4" />
          Send To
        </Label>
        <Input
          id="recipientEmail"
          type="email"
          value={recipientEmail}
          onChange={(e) => setRecipientEmail(e.target.value)}
          placeholder="Enter recipient email..."
        />
      </div>

      {/* Send Button */}
      <Button
        onClick={handleSendEmail}
        disabled={loading || !selectedType}
        className="w-full md:w-auto"
      >
        {loading ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Sending...
          </>
        ) : (
          <>
            <Send className="mr-2 h-4 w-4" />
            Send Test Email
          </>
        )}
      </Button>
    </div>
  )
}
